import React, { useEffect } from "react";
import PropTypes from "prop-types";

/**
 * Toast notification component
 * @param {Object} props
 * @param {string} props.message - Toast message
 * @param {string} props.type - Toast type: 'success', 'error', 'warning', 'info'
 * @param {Function} props.onClose - Callback when toast closes
 * @param {number} props.duration - Auto-close duration in ms (0 = no auto-close)
 */
function Toast({ message, type = "info", onClose, duration = 5000 }) {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        if (onClose) onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const typeStyles = {
    success: "bg-green-500 border-green-600",
    error: "bg-red-500 border-red-600",
    warning: "bg-orange-500 border-orange-600",
    info: "bg-blue-500 border-blue-600",
  };

  const style = typeStyles[type] || typeStyles.info;

  return (
    <div className="fixed top-4 right-4 z-50 animate-slideIn">
      <div
        className={`${style} text-white px-6 py-4 rounded-lg shadow-2xl border-l-4 max-w-md flex items-center justify-between gap-4`}
      >
        <p className="text-sm font-medium">{message}</p>
        {onClose && (
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 font-bold text-lg leading-none"
            aria-label="Close notification"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}

Toast.propTypes = {
  message: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["success", "error", "warning", "info"]),
  onClose: PropTypes.func,
  duration: PropTypes.number,
};

export default Toast;
